<template>
  <div class="vibrant-product-tabs">
    <!-- Vibrant Product Tab -->
    <div class="tab-content">
      <!-- Home Collection Toggle -->
      <div class="home-collection-toggle-wrapper">
        <div class="home-collection-label-wrapper">
          <span class="home-collection-toggle-text">Show Only Tests Available for Home Collection</span>
        </div>
        <div class="status-toggle">
          <span class="status-label" :class="{ active: !isDbsVersion }">OFF</span>
          <div class="toggle-switch" :class="{ 'active': isDbsVersion }" @click="toggleHomeCollection">
            <div class="toggle-track">
              <div class="toggle-thumb"></div>
            </div>
          </div>
          <span class="status-label" :class="{ active: isDbsVersion }">ON</span>
        </div>
      </div>

      <!-- OrderTestPanel - directly show VA/VW/Regenere/NutriProZ/Custom Panel/Shortcut Bundles tabs -->
      <div class="order-test-panel-content">
        <OrderTestPanel
          ref="orderTestPanelRef"
          :key="orderTestPanelKey"
          selectProp="isSelect"
          :orderTestSupportObj="orderTestSupportObj"
          isNotAllowedSelectProp="isNotAllowedSelect"
          :is-show-merch-tab-parent="false"
          :is-show-dbs-switch="false"
          :is-show-search-test="false"
          :display-item-data="displayItemData"
          :custom-panel-list="customPanelList"
          :highlight-list="highlightList"
          :is-show-highlight-tab="true"
          :is-show-shortcut-bundles-tab="true"
          :is-show-discount-panel-tab="false"
          @shortcut-selection-change="handleShortcutSelectionChange"
        />
      </div>
    </div>
  </div>
</template>

<script>
import OrderTestPanel from '@/views/va-orderTest/order-page/order-test-panel/index.vue';
import { mapState, mapGetters } from 'vuex';
import { handleException, handleBloodSalivaAddonException } from '@/utils/va-order-test/testAndPanelSelection/handleException';
import { handleDuplicateTests } from '@/utils/va-order-test/testAndPanelSelection/handleDuplicateTests';
import { handleBestDealSuggestion } from '@/utils/va-order-test/bestDealSuggestion';
import { applyConditionalDiscounts } from '@/utils/va-order-test';
import {
  HEAVY_METAL_VW_PRICE_THRESHOLD,
  HEAVY_METAL_VW_PRICE_THRESHOLD_NEW_VW_BETA
} from '@/utils/va-order-test/bestDealSuggestion/bestDealConstants.js';

export default {
  name: "VibrantProductTabs",
  components: {
    OrderTestPanel,
  },
  props: {
    cart: {
      type: Array,
      default: () => [],
    },
    highlightList: {
      type: Array,
      default: () => [],
    },
  },
  emits: ['selection-change', 'show-shortcut-bundles'],
  data() {
    return {
      orderTestPanelKey: 0,
    };
  },
  computed: {
    ...mapState('orderTest', ['dbsMap', 'bestDealSupportMapping', 'discountMap', 'isDbsVersion', 'isRestoringItems']),
    ...mapState('orderTest/exceptions', ['exceptionMap']),
    ...mapState('orderTest/promotions', ['seasonalPromotionList']),
    ...mapState('orderTest/user', ['userInfo']),
    ...mapState('orderTest/panels', {
      customPanelList: state => state.customPanelList,
      discountPanelList: state => state.discountPanelList,
      serumDiscountPanelList: state => state.serumDiscountPanelList,
      dbsDiscountPanelList: state => state.dbsDiscountPanelList,
    }),
    ...mapState('orderTest/tests', [
      'vaRootNodeList',
      'vwRootNodeList',
      'vaBfsList',
      'vwBfsList',
      'regenereIdList',
      'nutriprozIdList',
      'vwCategories',
    ]),
    ...mapGetters('orderTest', [
      'orderTestSupportObj',
      'selectedItemList',
      'selectedVwTestPrice',
      'selectedItemsPrice',
    ]),
    ...mapGetters('orderTest/tests', [
      'testList',
      'bfsTestList',
      'vwTestList',
      'serumCategorizedVwTests',
      'dbsCategorizedVwTests',
      'regenereNodeList',
      'nutriprozNodeList',
    ]),
    ...mapGetters('orderTest/panels', ['bundleList']),
    ...mapGetters('orderTest/favorites', ['itemsInSelectedShortcuts']),

    displayItemData() {
      return {
        bundleList: this.bundleList,
        vaRootNodeList: this.vaRootNodeList,
        vwRootNodeList: this.vwRootNodeList,
        regenereIdList: this.regenereIdList,
        nutriprozIdList: this.nutriprozIdList,
        vaBfsList: this.vaBfsList,
        vwBfsList: this.vwBfsList,
        bfsTestList: this.bfsTestList,
        testList: this.testList,
        vwTestList: this.vwTestList,
        serumCategorizedVwTests: this.serumCategorizedVwTests,
        dbsCategorizedVwTests: this.dbsCategorizedVwTests,
        regenereNodeList: this.regenereNodeList,
        nutriprozNodeList: this.nutriprozNodeList,
        discountPanelList: this.discountPanelList,
        serumDiscountPanelList: this.serumDiscountPanelList,
        dbsDiscountPanelList: this.dbsDiscountPanelList,
      };
    },

    totalPrice() {
      return this.selectedItemsPrice;
    },
    isAnyThreeDBSPromoStart() {
      return Boolean((this.seasonalPromotionList || []).find(x => x.discountName === '5 for $1000 Bundle'));
    },
  },
  watch: {
    selectedItemList: {
      handler(newList, oldList) {
        this.findBestDeal(newList, oldList || []);
        // Emit selected items to parent for cart integration
        this.$emit('selection-change', this.selectedItemList, this.totalPrice);
      },
      deep: true
    }
  },
  methods: {
    toggleHomeCollection() {
      const nextDbsToggle = !this.isDbsVersion;
      const orderPanelRef = this.$refs.orderTestPanelRef;
      if (orderPanelRef?.changeDbsVersion) {
        orderPanelRef.changeDbsVersion(nextDbsToggle);
      }
    },
    findBestDeal(newValue, oldValue) {
      // Skip during item restoration — display exactly what was passed in
      if (this.isRestoringItems) {
        return;
      }
      const isSame = newValue.length === oldValue.length
        && newValue.every(newV => oldValue.find(oldV => oldV.id ? oldV.id === newV.id : oldV.bundleId === newV.bundleId));
      if (isSame) {
        return;
      }
      const isNewVwBeta = this.userInfo && this.userInfo.beta_programs && this.userInfo.beta_programs.includes('new_vw');
      const itemList = this.selectedItemList
        .filter(i => i.category !== 'shortcut')
        .concat(this.itemsInSelectedShortcuts || []);
      applyConditionalDiscounts(this.testList, 'isSelect', itemList);
      handleException('isSelect', 'isNotAllowedSelect', this.orderTestSupportObj);
      if (isNewVwBeta) {
        handleBloodSalivaAddonException('isSelect', 'isNotAllowedSelect', this.orderTestSupportObj);
      }
      handleDuplicateTests('isSelect', this.orderTestSupportObj);
      if (newValue.length === 0) {
        return;
      }
      const heavyMetalThreshold = isNewVwBeta ? HEAVY_METAL_VW_PRICE_THRESHOLD_NEW_VW_BETA : HEAVY_METAL_VW_PRICE_THRESHOLD;
      const isHeavyMetalSpecialPrice = this.selectedVwTestPrice > heavyMetalThreshold;
      handleBestDealSuggestion(
        'isSelect',
        this.totalPrice,
        this.orderTestSupportObj,
        this.discountMap,
        this.isDbsVersion,
        this.isAnyThreeDBSPromoStart,
        isHeavyMetalSpecialPrice,
        isNewVwBeta
      );
    },
    removeSelectedItemFromCart(item) {
      if (!item || !item.category) {
        return;
      }
      switch (item.category) {
        case 'discountPanel':
          this.$store.dispatch('orderTest/removeSelectedDiscountPanel', { bundleId: item.bundleId });
          break;
        case 'customPanel':
          this.$store.dispatch('orderTest/removeSelectedCustomPanel', { bundleId: item.bundleId });
          break;
        case 'xChooseY':
          this.$store.dispatch('orderTest/removeSelectedMultiChoosePanel', { bundleId: item.bundleId });
          break;
        case 'seasonalPromo':
          this.$store.dispatch('orderTest/removeSelectedSeasonalPromo', item);
          break;
        case 'shortcut':
          this.$store.dispatch('orderTest/removeSelectedShortcut', { id: item.id });
          break;
        default:
          this.$store.dispatch('orderTest/removeSelectedTest', { id: item.id });
          break;
      }
    },
    handleShortcutSelectionChange(selectedList, totalPrice) {
      // Emit to parent for cart integration
      this.$emit('selection-change', selectedList, totalPrice);
    },
  },
};
</script>

<style scoped>
.vibrant-product-tabs {
  width: 100%;
  font-family: 'Inter', sans-serif;
}

.tab-content {
  width: 100%;
}

.home-collection-toggle-wrapper {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 12px;
  background: var(--MessageBlue_BG, #E8F4FD);
  border-radius: 4px;
  margin-bottom: 12px;
}

.home-collection-label-wrapper {
  display: flex;
  align-items: center;
  gap: 8px;
}

.home-collection-toggle-text {
  font-size: 14px;
  color: #374957;
}

.status-toggle {
  display: flex;
  align-items: center;
  gap: 8px;
}

.status-label {
  font-size: 12px;
  color: #999;
  font-weight: 500;
}

.status-label.active {
  color: #004879;
}

.toggle-switch {
  width: 44px;
  height: 24px;
  background: #c4c4c4;
  border-radius: 12px;
  cursor: pointer;
  position: relative;
  transition: background 0.2s ease;
}

.toggle-switch.active {
  background: #004879;
}

.toggle-track {
  width: 100%;
  height: 100%;
  position: relative;
}

.toggle-thumb {
  width: 20px;
  height: 20px;
  background: white;
  border-radius: 50%;
  position: absolute;
  top: 2px;
  left: 2px;
  transition: left 0.2s ease;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
}

.toggle-switch.active .toggle-thumb {
  left: 22px;
}

.tab-content {
  min-height: 300px;
}

.order-test-panel-content {
  padding: 0 4px;
}

.selected-items-summary {
  margin-top: 16px;
  padding: 12px 16px;
  background: #f0f7ff;
  border: 1px solid #004879;
  border-radius: 8px;
}

.summary-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.summary-title {
  font-size: 14px;
  font-weight: 600;
  color: #004879;
}

.summary-price {
  font-size: 16px;
  font-weight: 700;
  color: #004879;
}
</style>
